/**
 * Global form submit guard:
 * - Disables submit buttons while a form is submitting
 * - Blocks double-submit
 * - Unlocks after API activity settles (or short timeout if no API call)
 *
 * Opt out: <form data-skip-submit-guard>
 */
import {
  getHttpInflightCount,
  onHttpInflightChange,
} from "./httpInflight";

const LOCK_ATTR = "hmsSubmitLocked";
const FORM_CLASS = "hms-form-submitting";

const isSubmitButton = (el) => {
  if (!(el instanceof HTMLElement)) return false;
  if (el.tagName === "INPUT") {
    return String(el.getAttribute("type") || "").toLowerCase() === "submit";
  }
  if (el.tagName !== "BUTTON") return false;
  const type = String(el.getAttribute("type") || "submit").toLowerCase();
  return type === "submit";
};

const getSubmitButtons = (form) =>
  Array.from(form.querySelectorAll("button, input[type='submit']")).filter(
    isSubmitButton
  );

const lockForm = (form) => {
  if (form.dataset[LOCK_ATTR] === "1") return;
  form.dataset[LOCK_ATTR] = "1";
  form.classList.add(FORM_CLASS);
  getSubmitButtons(form).forEach((btn) => {
    btn.dataset.hmsPrevDisabled = btn.disabled ? "1" : "0";
    btn.disabled = true;
    btn.setAttribute("aria-busy", "true");
  });
};

const unlockForm = (form) => {
  if (!form || form.dataset[LOCK_ATTR] !== "1") return;
  form.dataset[LOCK_ATTR] = "0";
  form.classList.remove(FORM_CLASS);
  getSubmitButtons(form).forEach((btn) => {
    if (btn.dataset.hmsPrevDisabled === "0") {
      btn.disabled = false;
    }
    btn.removeAttribute("aria-busy");
    delete btn.dataset.hmsPrevDisabled;
  });
};

const watchUnlock = (form) => {
  const baseline = getHttpInflightCount();
  const started = Date.now();
  let finished = false;
  let sawIncrease = false;

  const finish = () => {
    if (finished) return;
    finished = true;
    try {
      unsubscribe();
    } catch {
      // ignore
    }
    clearInterval(pollId);
    clearTimeout(safetyId);
    try {
      observer.disconnect();
    } catch {
      // ignore
    }
    // Let Formik/React settle before unlocking native DOM disabled flag
    window.setTimeout(() => unlockForm(form), 120);
  };

  const unsubscribe = onHttpInflightChange((count) => {
    if (count > baseline) sawIncrease = true;
    if (sawIncrease && count <= baseline) finish();
  });

  const pollId = window.setInterval(() => {
    const count = getHttpInflightCount();
    if (count > baseline) sawIncrease = true;
    if (sawIncrease && count <= baseline) {
      finish();
      return;
    }
    // Validation-only / no network: unlock shortly
    if (!sawIncrease && Date.now() - started > 900) {
      finish();
    }
  }, 80);

  const safetyId = window.setTimeout(finish, 45000);

  const observer = new MutationObserver(() => {
    if (!document.body.contains(form)) finish();
  });
  observer.observe(document.body, { childList: true, subtree: true });
};

/**
 * Also lock common non-type=submit primary actions that trigger formik.submitForm().
 */
const looksLikeSubmitAction = (btn) => {
  if (!(btn instanceof HTMLButtonElement)) return false;
  if (btn.type && btn.type !== "button") return false;
  const label = `${btn.textContent || ""} ${btn.getAttribute("aria-label") || ""}`
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
  if (!label) return false;
  if (
    label.includes("cancel") ||
    label.includes("back") ||
    label.includes("close") ||
    label.includes("clear") ||
    label.includes("reset") ||
    label.includes("add more") ||
    label.includes("remove")
  ) {
    return false;
  }
  return (
    label === "submit" ||
    label.startsWith("submit ") ||
    label.includes("save") ||
    label.includes("create") ||
    label.includes("update") ||
    label.includes("register") ||
    label.includes("assign") ||
    label.includes("confirm") ||
    label.includes("sign in") ||
    label.includes("login")
  );
};

export const installFormSubmitGuard = () => {
  if (typeof document === "undefined") return;
  if (window.__hmsFormSubmitGuardInstalled) return;
  window.__hmsFormSubmitGuardInstalled = true;

  document.addEventListener(
    "submit",
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.hasAttribute("data-skip-submit-guard")) return;

      if (form.dataset[LOCK_ATTR] === "1") {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      lockForm(form);
      watchUnlock(form);
    },
    true
  );

  // type="button" primary actions inside forms (e.g. formik.submitForm())
  document.addEventListener(
    "click",
    (event) => {
      const btn = event.target?.closest?.("button");
      if (!btn || btn.disabled) return;
      const form = btn.form || btn.closest("form");
      if (!form || !(form instanceof HTMLFormElement)) return;
      if (form.hasAttribute("data-skip-submit-guard")) return;
      if (form.dataset[LOCK_ATTR] === "1") {
        // already submitting — block duplicate primary actions
        if (looksLikeSubmitAction(btn) || isSubmitButton(btn)) {
          event.preventDefault();
          event.stopPropagation();
        }
        return;
      }
      if (!looksLikeSubmitAction(btn)) return;

      // Defer lock to next tick so onClick can start submitForm/validate first
      window.setTimeout(() => {
        if (!document.body.contains(form)) return;
        // If form submit event already locked, nothing to do
        if (form.dataset[LOCK_ATTR] === "1") return;
        // Lock if formik/local loading started submitting without HTML submit event
        lockForm(form);
        watchUnlock(form);
      }, 0);
    },
    true
  );
};

export default installFormSubmitGuard;
